import { observer } from 'mobx-react-lite';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CURRENCY_CONVERTER_EVENT } from '../../constants';
import { useThemeContext } from '../../global-state/themeContext';
import { useStoreInformation } from '../../hooks';
import { useCurrencyTools } from '../../hooks/useCurrencyTools';
import useEventTracker from '../../hooks/useEventTracker';
import { useCurrencyStore } from '../../stores/useCurrencyStore';
import { Typography } from '../../stories/atoms';
import { RowFlexContainer } from '../../stories/atoms/containerV2/ContainerV2';
import { DropdownMenu } from '../../stories/molecules';
import { multiStoreMenuItemTranslation } from '../../translations';
import { currencySymbolMap, getFlagUrlFromCurrency } from '../../utils/language';

export const CurrencyConverter: FC = observer(() => {
  const { t } = useTranslation();
  const trackEvent = useEventTracker();
  const { mode } = useThemeContext();
  const { currencyCode: storeCurrency } = useStoreInformation();
  const { selectedCurrency, latestExchangeRates } = useCurrencyTools();
  const setSelectedCurrency = useCurrencyStore((state) => state.setUserCurrency);

  const getExchangeRateCurrencies = () => {
    let exchangeRateCurrencies: string[] = [];

    if (!latestExchangeRates) {
      exchangeRateCurrencies = storeCurrency ? [storeCurrency] : [];
    } else {
      exchangeRateCurrencies = Object.keys(latestExchangeRates);
      if (storeCurrency && !exchangeRateCurrencies.includes(storeCurrency.toUpperCase())) {
        exchangeRateCurrencies.push(storeCurrency);
      }
    }

    return exchangeRateCurrencies;
  };

  const exchangeRateCurrencies = getExchangeRateCurrencies();

  const getCurrencyMenuItems = useCallback(() => {
    return exchangeRateCurrencies.map((currency) => ({
      key: currency,
      item: (
        <RowFlexContainer alignItems="center" gap={[3]} padding={[2, 0]} width="100%">
          <img width={28} height={28} src={getFlagUrlFromCurrency(currency)} alt={currency} />
          <RowFlexContainer justifyContent="between" width="100%">
            <Typography>{currency}</Typography>
            <Typography>{currencySymbolMap[currency]}</Typography>
          </RowFlexContainer>
        </RowFlexContainer>
      ),
    }));
  }, [selectedCurrency, latestExchangeRates]);

  const handleCurrency = (currency: string) => {
    setSelectedCurrency(currency);

    trackEvent(CURRENCY_CONVERTER_EVENT, 'click', [{ name: 'currency', stringValue: currency }]);
  };

  return (
    <>
      {selectedCurrency && (
        <DropdownMenu
          title={t(
            multiStoreMenuItemTranslation.switchCurrency.key,
            multiStoreMenuItemTranslation.switchCurrency.defaultValue,
          )}
          dropdownLabel=""
          menuPosition="left"
          menuItems={getCurrencyMenuItems()}
          initialSelected={[selectedCurrency]}
          onSelect={(selectedItemKey) => {
            handleCurrency(selectedItemKey[0] as string);
          }}
          buttonContentProps={{
            isDisabled: latestExchangeRates === null,
            iconOptions: {
              customIcon: (
                <RowFlexContainer
                  alignItems="center"
                  gap={[1]}
                  hasBorder={latestExchangeRates !== null}
                  padding={[1]}
                  borderRadius={[1]}
                  borderColor={mode === 'dark' ? 'white' : 'primary'}
                >
                  {!!exchangeRateCurrencies.length && (
                    <>
                      <Typography type="button">{selectedCurrency}</Typography>
                      <Typography type="button">{currencySymbolMap[selectedCurrency]}</Typography>
                    </>
                  )}
                </RowFlexContainer>
              ),
            },
          }}
          backgroundColor={{ hover: 'baseGray', selected: 'primary', selectedHover: 'secondaryTextGray' }}
          isOnlyIcon
          minWidth="0px"
        />
      )}
    </>
  );
});
